import PageLayout from '../components/PageLayout'
import PageHero from '../components/PageHero'
import ProseContent from '../components/ProseContent'

const sections = [
  {
    heading: 'Who this policy applies to',
    body: (
      <>
        <p>
          This Privacy Policy covers personal information Oak Platforms Inc. ("Oak," "we," or "us") collects and processes when you:
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Visit oakplatforms.com or request a demo of our platform.</li>
          <li>Operate an Oak-powered marketplace as a moderator or their delegated team.</li>
          <li>Use an Oak-powered marketplace as a member, buyer, or seller.</li>
          <li>Interact with Oak support, sales, or developer relations.</li>
        </ul>
        <p>
          Where a moderator runs a marketplace on Oak, the moderator is the <em>controller</em> of member data within that marketplace, and Oak acts as a <em>processor</em> on their behalf. For our own website and platform operations, Oak is the controller.
        </p>
      </>
    ),
  },
  {
    heading: 'Information we collect',
    body: (
      <>
        <p>We collect only what we need to run Oak well. That falls into four categories:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Account information</strong> — name, email, role, and authentication identifiers for moderators and members.
          </li>
          <li>
            <strong>Transaction data</strong> — listings, orders, payment metadata, and payout records, processed through our payment partners (Stripe and Stripe Connect).
          </li>
          <li>
            <strong>Usage data</strong> — product interactions, device and browser information, IP address, and diagnostic logs required to operate and secure the platform.
          </li>
          <li>
            <strong>Content you create</strong> — listings, messages, reviews, and comments you post inside an Oak marketplace.
          </li>
        </ul>
        <p>
          We do not sell personal information, and we do not use personal information for advertising across third-party networks.
        </p>
      </>
    ),
  },
  {
    heading: 'How we use information',
    body: (
      <>
        <p>We use the information described above to:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Provide the Oak platform, including payments, messaging, analytics, and agentic discovery.</li>
          <li>Authenticate users, prevent fraud, and enforce marketplace rules.</li>
          <li>Communicate with you about your account, product updates, and security notices.</li>
          <li>Produce aggregated insights for Cortex, with personal identifiers removed.</li>
          <li>Comply with legal obligations and respond to lawful requests.</li>
        </ul>
        <p>
          When we rely on <strong>legitimate interests</strong> as a legal basis (for example, to keep the platform secure), we balance those interests against your rights and document the outcome.
        </p>
      </>
    ),
  },
  {
    heading: 'Who we share information with',
    body: (
      <>
        <p>We share personal information only with:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Subprocessors</strong> that help us run Oak (hosting, payments, email, logging, and analytics). A current list is maintained on our GDPR page.
          </li>
          <li>
            <strong>Marketplace moderators</strong>, to the extent their members interact with their marketplace — the moderator is the data controller for that relationship.
          </li>
          <li>
            <strong>Legal and safety</strong> — when required by law, or to protect the rights, safety, and property of Oak, our users, or the public.
          </li>
          <li>
            <strong>Business transfers</strong> — in the event of a merger, acquisition, or sale of assets, with notice to affected users.
          </li>
        </ul>
      </>
    ),
  },
  {
    heading: 'Your rights',
    body: (
      <>
        <p>
          Depending on where you live, you may have the right to access, correct, export, delete, or restrict the processing of your personal information. You can also object to certain processing and withdraw consent at any time.
        </p>
        <p>
          To exercise these rights, email <a href="mailto:privacy@oakplatforms.com" className="text-oak-black underline underline-offset-2">privacy@oakplatforms.com</a>. For data inside a specific Oak-powered marketplace, contact the moderator directly — they are the controller of that data.
        </p>
      </>
    ),
  },
  {
    heading: 'Data retention',
    body: (
      <>
        <p>
          We keep personal information for as long as needed to provide the platform, meet legal or tax obligations, resolve disputes, and enforce our agreements. When you close your account, we delete or anonymize personal information on a schedule documented in our internal retention policy, with specific financial records retained for the period required by law.
        </p>
      </>
    ),
  },
  {
    heading: 'International transfers',
    body: (
      <>
        <p>
          Oak is headquartered in the United States and processes data in the US and EU. When personal information is transferred outside your region, we rely on approved transfer mechanisms — including the EU Standard Contractual Clauses and the UK International Data Transfer Addendum — alongside technical safeguards such as encryption in transit and at rest.
        </p>
      </>
    ),
  },
  {
    heading: 'Security',
    body: (
      <>
        <p>
          We take the security of our customers' data seriously. Oak operates a documented information-security program covering encryption, access control, secure software development, and incident response. We continuously monitor our systems, test for vulnerabilities, and disclose confirmed incidents to affected customers without undue delay.
        </p>
        <p>
          Report a vulnerability to <a href="mailto:security@oakplatforms.com" className="text-oak-black underline underline-offset-2">security@oakplatforms.com</a>. We publish guidelines for responsible disclosure and do not pursue good-faith researchers.
        </p>
      </>
    ),
  },
  {
    heading: "Children's privacy",
    body: (
      <>
        <p>
          Oak is not directed to children under 13 (or the equivalent age in your jurisdiction). We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, contact us and we will delete it.
        </p>
      </>
    ),
  },
  {
    heading: 'Changes to this policy',
    body: (
      <>
        <p>
          We update this policy from time to time. Material changes will be announced in-product and by email to account owners at least 30 days before they take effect. The "Last updated" date at the top of this page reflects the current version.
        </p>
      </>
    ),
  },
  {
    heading: 'Contact us',
    body: (
      <>
        <p>
          Privacy questions: <a href="mailto:privacy@oakplatforms.com" className="text-oak-black underline underline-offset-2">privacy@oakplatforms.com</a>
        </p>
        <p>
          Postal: Oak Platforms Inc., 228 Park Ave S, PMB 84351, New York, NY 10003, United States.
        </p>
      </>
    ),
  },
]

export default function PrivacyPage() {
  return (
    <PageLayout>
      <PageHero
        eyebrow="Privacy Policy"
        title="What we collect, why, and what you can do about it."
        description="We take the stewardship of your data seriously. This policy explains our practices in plain language."
      />
      <ProseContent
        updated="April 1, 2026"
        intro={
          <p>
            This Privacy Policy applies to Oak Platforms Inc. and describes how we handle personal information across our website, platform, and marketplaces built with Oak.
          </p>
        }
        sections={sections}
      />
    </PageLayout>
  )
}
